import { useEffect, useState, useCallback } from "react";

/**
 * OneTrust standard consent categories.
 * Mirrors what the OneTrust dashboard exposes via OnetrustActiveGroups.
 *
 * These IDs (C0001..C0005) are IAB-standard and used across multiple CMPs,
 * so the same values can be reused if/when the underlying vendor changes.
 */
export const ONE_TRUST_CATEGORIES = {
  STRICTLY_NECESSARY: "C0001",
  PERFORMANCE: "C0002",
  FUNCTIONAL: "C0003",
  TARGETING: "C0004",
  SOCIAL_MEDIA: "C0005",
} as const;

export type OneTrustCategory =
  (typeof ONE_TRUST_CATEGORIES)[keyof typeof ONE_TRUST_CATEGORIES];

/**
 * Reads the user's active OneTrust consent groups and reacts to changes.
 *
 * Strategy:
 *   - On mount, read the global OnetrustActiveGroups string.
 *   - Subscribe to OneTrustGroupsUpdated to react when the user changes
 *     consent (in this tab or in another tab via cookie sync).
 *
 * Why a hook rather than parsing the cookie directly:
 *   - The OneTrust SDK normalizes the cookie format and exposes a stable API.
 *   - We avoid coupling our code to OneTrust's cookie schema.
 *
 * Strictly Necessary (C0001) is always considered granted.
 */
export function useOneTrustConsent() {
  const [groups, setGroups] = useState<string[]>([]);

  useEffect(() => {
    if (globalThis.window === undefined) return;

    const readGroups = (): string[] => {
      const raw = (globalThis.window as unknown as { OnetrustActiveGroups?: string })
        .OnetrustActiveGroups;
      if (!raw) return [];
      return raw.split(",").filter(Boolean);
    };

    setGroups(readGroups());

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<string[] | string>).detail;
      if (Array.isArray(detail)) {
        setGroups(detail.filter(Boolean));
      } else if (typeof detail === "string") {
        setGroups(detail.split(",").filter(Boolean));
      } else {
        setGroups(readGroups());
      }
    };

    globalThis.window.addEventListener("OneTrustGroupsUpdated", handler);
    return () => {
      globalThis.window.removeEventListener("OneTrustGroupsUpdated", handler);
    };
  }, []);

  const hasConsent = useCallback(
    (category: OneTrustCategory): boolean => {
      if (category === ONE_TRUST_CATEGORIES.STRICTLY_NECESSARY) return true;
      return groups.includes(category);
    },
    [groups],
  );

  return { groups, hasConsent };
}
